import React, { useState } from "react";
import {
  FiCamera,
  FiEye,
  FiKey,
  FiUser,
  FiMail,
  FiPhone,
  FiSave,
} from "react-icons/fi";
import SelectDropdown from "../shared/SelectDropdown";
import Input from "../shared/Input";
import "./TabProfile.css";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";

export const formatPhone = (phone = "") => {
  let digits = phone.toString().replace(/\D/g, "");

  // +90 ile gelenler
  if (digits.startsWith("90") && digits.length <= 12) {
    return `+90 (${digits.slice(2, 5)}) ${digits.slice(5, 8)} ${digits.slice(8, 10)} ${digits.slice(10, 12)}`.trim();
  }

  // 0 ile başlayanlar
  if (digits.startsWith("0")) {
    return `0 (${digits.slice(1, 4)}) ${digits.slice(4, 7)} ${digits.slice(7, 9)} ${digits.slice(9, 11)}`.trim();
  }

  // 5 ile başlayanlar
  if (digits.startsWith("5")) {
    return `0 (${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`.trim();
  }

  return digits;
};

const TabCustomerProfile = ({
  customerData,
  setCustomerData,
  rolesOptions, // Rol seçenekleri
  languagesOptions, // Dil seçenekleri
  countries, // Ülke seçenekleri
  avatarList, // Avatar listesi
  handleSubmit, // Kaydet butonuna basılınca yapılacak işlem
}) => {
  // Çeviri ile ilgili
  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, defaultValue) => translations[key] || defaultValue;

  const selectedRoleOption =
    rolesOptions.find((o) => o.value === customerData?.role) || null;
  const selectedLangOption =
    languagesOptions.find((o) => o.value === customerData?.language) || null;
  const selectedCountryOption =
    countries.find((o) => o.value === customerData?.country) || null;

  // Eyalet/şehir kaldırıldı
  // Sadece country/role/language kaldı

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const toFullUrl = (path) =>
    path.startsWith("http")
      ? path
      : `${process.env.REACT_APP_API_BASE_URL}${path}`;

  // Avatar URL (varsayılan)
  const avatarUrl = customerData?.profileImage
    ? `${process.env.REACT_APP_API_BASE_URL}${customerData.profileImage}`
    : `${process.env.REACT_APP_API_BASE_URL}/uploads/avatars/default.svg`;

  // Her input değiştiğinde state günceller
  const handleInputChange = (e) => {
    if (!setCustomerData) return;
    const { name, value } = e.target;
    setCustomerData((prev) => ({ ...prev, [name]: value }));
  };

  // Avatar seçimi
  const handleAvatarSelect = (avatar) => {
    if (!setCustomerData) return;
    setCustomerData((prev) => ({ ...prev, profileImage: avatar }));
    setShowAvatarModal(false);
  };

  const handleRoleSelect = (option) => {
    if (!option || !option.value) return;
    setCustomerData((prev) => ({ ...prev, role_name: option.label }));
  };

  // Dil seçimi
  const handleLanguageSelect = (option) => {
    if (!setCustomerData) return;
    setCustomerData((prev) => ({ ...prev, language: option.value }));
  };

  // Ülke seçimi
  const handleCountrySelect = (option) => {
    if (!setCustomerData) return;
    setCustomerData((prev) => ({ ...prev, country: option.value }));
  };

  return (
    <div className="tab-pane fade show active" id="profileTab" role="tabpanel">
      {/* Kişisel Bilgiler */}
      <div className="card-body personal-info">
        <div className="mb-4 d-flex align-items-center justify-content-between">
          <h5 className="fw-bold mb-0 me-4">
            <span className="d-block mb-2">
              {getText("ADMIN_INFO")}
            </span>
          </h5>
        </div>

        {/* Avatar */}
        <div className="row mb-4 align-items-center">
          <div className="col-lg-2">
            <label className="fw-semibold">
              {getText("TABLE_PROFILE_IMAGE_LABEL")}
            </label>
          </div>
          <div className="col-lg-8 d-flex align-items-center gap-3">
            <img
              src={avatarUrl}
              alt="Avatar"
              className="rounded-circle avatar-preview"
              style={{ width: "80px", height: "80px", objectFit: "cover" }}
              onClick={() => setShowAvatarModal(true)}
            />
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => setShowAvatarModal(true)}
            >
              <FiCamera className="me-2" /> {getText("PROFILE_SELECT")}
            </button>
          </div>
        </div>

        {/* Ad */}
        <Input
          icon={<FiUser />}
          label={getText("TABLE_FIRST_NAME_LABEL")}
          name="firstName"
          placeholder="Adınız"
          value={customerData?.firstName || ""}
          onChange={handleInputChange}
        />

        {/* Soyad */}
        <Input
          icon={<FiUser />}
          label={getText("TABLE_LAST_NAME_LABEL")}
          name="lastName"
          placeholder="Soyadınız"
          value={customerData?.lastName || ""}
          onChange={handleInputChange}
        />

        {/* E-posta */}
        <Input
          icon={<FiMail />}
          label={getText("TABLE_EMAIL_LABEL")}
          name="email"
          type="email"
          placeholder="example@example.com"
          value={customerData?.email || ""}
          onChange={handleInputChange}
        />

        <Input
          icon={<FiPhone />}
          label={getText("TABLE_PHONE_LABEL")}
          name="phone"
          type="tel"
          placeholder="+90 (555) 555 00 00"
          value={formatPhone(customerData?.phone || "")} // 👈 formatlı göster
          onChange={(e) => {
            const raw = e.target.value.replace(/\D/g, ""); // 👈 sadece rakam
            setCustomerData((prev) => ({
              ...prev,
              phone: raw,
            }));
          }}
          autoComplete="off"
        />
      </div>

      <hr className="my-0" />

      {/* Ek Bilgiler */}
      <div className="card-body additional-info">
        <div className="mb-4 d-flex align-items-center justify-content-between">
          <h5 className="fw-bold mb-0 me-4">
            <span className="d-block mb-2">
              {getText("GENERAL_INFO")}
            </span>
          </h5>
        </div>

        {/* Country */}
        <div className="row mb-4 align-items-center">
          <div className="col-lg-2">
            <label className="fw-semibold">{getText("TABLE_COUNTRY_LABEL")}</label>
          </div>
          <div className="col-lg-8">
            <SelectDropdown
              options={countries}
              selectedOption={selectedCountryOption}
              onSelectOption={handleCountrySelect}
              defaultSelect={getText("COUNTRY_SELECT")}
            />
          </div>
        </div>

        {/* Language */}
        <div className="row mb-4 align-items-center">
          <div className="col-lg-2">
            <label className="fw-semibold">{getText("TABLE_LANGUAGE_LABEL")}</label>
          </div>
          <div className="col-lg-8">
            <SelectDropdown
              options={languagesOptions}
              selectedOption={selectedLangOption}
              onSelectOption={handleLanguageSelect}
              defaultSelect={getText("LANGUAGE_SELECT")}
            />
          </div>
        </div>
      </div>

      <hr className="my-0" />

      {/* Şifre Bilgileri */}
      <div className="card-body pass-info">
        <div className="mb-4 d-flex align-items-center justify-content-between">
          <h5 className="fw-bold mb-0 me-4">
            <span className="d-block mb-2">
              {getText("PROFILE_PASSWORD_INFO")}
            </span>
          </h5>
        </div>

        {/* Password */}
        <div className="row mb-4 align-items-center">
          <div className="col-lg-2 fw-semibold">
            {getText("PROFILE_PASSWORD")}
          </div>
          <div className="col-lg-8">
            <div className="input-group">
              <div className="input-group-text">
                <FiKey />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                className="form-control"
                name="password"
                 placeholder={getText("PROFILE_PASSWORD_PLACEHOLDER")}
                value={customerData?.password || ""}
                onChange={handleInputChange}
              />
              <div
                className="input-group-text border-start bg-gray-2 c-pointer"
                onClick={() => setShowPassword(!showPassword)}
              >
                <FiEye />
              </div>
            </div>
          </div>
        </div>

        {/* Confirm Password */}
        <div className="row mb-4 align-items-center">
          <div className="col-lg-2 fw-semibold">
            {getText("PROFILE_CONFIRM_PASSWORD")}
          </div>
          <div className="col-lg-8">
            <div className="input-group">
              <div className="input-group-text">
                <FiKey />
              </div>
              <input
                type={showConfirmPassword ? "text" : "password"}
                className="form-control"
                name="confirmPassword"
                placeholder={getText("PROFILE_CONFIRM_PASSWORD_PLACEHOLDER")}
                value={customerData?.confirmPassword || ""}
                onChange={handleInputChange}
              />
              <div
                className="input-group-text border-start bg-gray-2 c-pointer"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                <FiEye />
              </div>
            </div>
          </div>
        </div>

        {/* Kaydet Butonu */}
        <div className="d-flex justify-content-center mt-4">
          <button
            className="btn btn-primary px-3 py-2.5 fw-bold"
            onClick={handleSubmit}
          >
            <FiSave className="me-2" /> {getText("SAVE_BTN")}
          </button>
        </div>
      </div>

      {/* Avatar Seçim Modalı */}
      {showAvatarModal && (
        <div
          className="avatar-modal-overlay"
          onClick={() => setShowAvatarModal(false)}
        >
          <div
            className="avatar-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="mb-3 fw-bold">Avatar Seç</h4>
            <div className="avatar-selection-container">
              {avatarList?.map((avatar, index) => (
                <img
                  key={index}
                  src={toFullUrl(avatar)}
                  alt="Avatar"
                  className={`avatar-img ${
                    customerData?.profileImage === avatar ? "selected" : ""
                  }`}
                  onClick={() => {
                    handleAvatarSelect(
                      avatar.replace(
                        `${process.env.REACT_APP_API_BASE_URL}`,
                        "",
                      ),
                    );
                  }}
                />
              ))}
            </div>
            <button
              className="close-button mt-3"
              onClick={() => setShowAvatarModal(false)}
            >
              {getText("CLOSE")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TabCustomerProfile;
